import { inject, Injectable, OnDestroy } from '@angular/core'
import { MatSnackBar, MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar'
import { TranslateService } from '@ngx-translate/core'
import { Observable, Subject } from 'rxjs'
import { distinctUntilChanged, take, takeUntil } from 'rxjs/operators'

@Injectable()
export class BalanceSnackbarController implements OnDestroy {
  private readonly snackBar = inject(MatSnackBar)
  private readonly translate = inject(TranslateService)
  private readonly destroy$ = new Subject<void>()
  private ref?: MatSnackBarRef<unknown>
  // Guards against a second attach() stacking subscribers — per-call subs would drive `this.ref` from multiple closures concurrently.
  private attached = false

  // Hosts compose `visible$` from state + form validity (typically `needsPricing && form.valid`) rather than the controller reaching into the form.
  attach(visible$: Observable<boolean>, onAction: () => void): void {
    if (this.attached) {
      return
    }
    this.attached = true
    visible$
      .pipe(distinctUntilChanged(), takeUntil(this.destroy$))
      .subscribe(visible => {
        if (visible && !this.ref) {
          this.open(onAction)
        } else if (!visible) {
          this.dismiss()
        }
      })
  }

  private open(onAction: () => void): void {
    // openFromComponent instead of open(): Veracode misreads snackBar.open(translated) as fs.open (CWE-73)
    const message = this.translate.instant('balance.snackbar.needs-pricing')
    this.ref = this.snackBar.openFromComponent(SimpleSnackBar, {
      duration: 0,
      verticalPosition: 'top',
      data: { message, action: this.translate.instant('balance.snackbar.calculate-and-save') },
    })
    // Honour user dismissals: without this, `this.ref` stays non-null after
    // the snackbar is dismissed by the user/timeout, and the open-side guard
    // (`!this.ref`) silently refuses to re-open on the next needsPricing toggle.
    this.ref
      .afterDismissed()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(() => { this.ref = undefined })
    this.ref
      .onAction()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(() => {
        this.ref = undefined
        onAction()
      })
  }

  dismiss(): void {
    if (this.ref) {
      this.ref.dismiss()
      this.ref = undefined
    }
  }

  ngOnDestroy(): void {
    this.dismiss()
    this.destroy$.next()
    this.destroy$.complete()
  }
}
